import { ElectedRepresentativeType } from "@boark/shared/types/elected-representative-type";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import { Locale } from "../locale/locale.type";
import { useLocale } from "../stores/locale";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Input } from "./input";
import { Select } from "./select";

export type UserFormValues = {
  email: string;
  firstName: string;
  lastName: string;
  sendInvite: boolean;
  isOwner: boolean;
  electedRepresentativeType: ElectedRepresentativeType | "";
  password: string;
};

export function electedRepresentativeTypeOptions(
  t: Locale,
): { label: string; value: ElectedRepresentativeType }[] {
  return [
    { label: t.user.electedRepresentativeType.boardMember, value: "boardMember" },
    {
      label: t.user.electedRepresentativeType.alternateBoardMember,
      value: "alternateBoardMember",
    },
    {
      label: t.user.electedRepresentativeType.representative,
      value: "representative",
    },
    { label: t.user.electedRepresentativeType.other, value: "other" },
  ];
}

export function UserForm({
  mode,
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
}: {
  mode: "create" | "edit";
  onCancel: () => void;
  onChange: (values: UserFormValues) => void;
  onSubmit: () => void;
  values: UserFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const { locale: t, get } = useLocale();
  const isDisabled = disabled || loading;
  const showPasswordField = mode === "edit" || !values.sendInvite;

  const isValid = useMemo(() => {
    if (values.email.trim() === "") {
      return false;
    }
    if (mode === "create" && !values.sendInvite && values.password.length < 8) {
      return false;
    }
    if (mode === "edit" && values.password.length > 0 && values.password.length < 8) {
      return false;
    }
    return true;
  }, [values, mode]);

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4 min-w-md"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex flex-col gap-4">
        <Input
          required
          disabled={isDisabled}
          label={t.user.email}
          type="email"
          value={values.email}
          onChange={(e) => onChange({ ...values, email: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={get("user.firstName")}
          type="text"
          value={values.firstName}
          onChange={(e) => onChange({ ...values, firstName: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={get("user.lastName")}
          type="text"
          value={values.lastName}
          onChange={(e) => onChange({ ...values, lastName: e.target.value })}
        />
        {mode === "create" && (
          <Select
            disabled={isDisabled}
            label={t.user.type}
            options={[
              { label: t.auth.memberTypeMember, value: "member" },
              { label: t.auth.memberTypeOwner, value: "owner" },
            ]}
            value={values.isOwner ? "owner" : "member"}
            onChange={(value) =>
              onChange({ ...values, isOwner: value === "owner" })
            }
          />
        )}
        <Select
          allowEmpty
          disabled={isDisabled}
          label={t.user.electedRepresentativeType.label}
          options={electedRepresentativeTypeOptions(t)}
          placeholder={t.user.electedRepresentativeType.none}
          value={values.electedRepresentativeType}
          onChange={(value) =>
            onChange({
              ...values,
              electedRepresentativeType: value as ElectedRepresentativeType | "",
            })
          }
        />
        {mode === "create" && (
          <Checkbox
            checked={values.sendInvite}
            className="flex items-center gap-2"
            disabled={isDisabled}
            label={t.user.sendInvite}
            onClick={(checked) =>
              onChange({ ...values, sendInvite: !checked })
            }
          />
        )}
        {showPasswordField && (
          <Input
            disabled={isDisabled}
            label={t.user.password}
            placeholder={
              mode === "edit" ? t.user.passwordPlaceholderKeep : undefined
            }
            required={mode === "create" && !values.sendInvite}
            type="password"
            value={values.password}
            onChange={(e) => onChange({ ...values, password: e.target.value })}
          />
        )}
      </div>

      {hasError && (
        <div className="flex items-center gap-2 text-red-600 mt-4">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{mode === "create" ? t.user.createError : t.user.editError}</span>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <Button disabled={isDisabled} type="button" onClick={onCancel}>
          {t.cancel}
        </Button>
        <Button disabled={isDisabled || !isValid} loading={loading} type="submit">
          {t.send.submit}
        </Button>
      </div>
    </form>
  );
}
